import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { AlertCircle, KeyRound } from 'lucide-react';
import { generateSalt, hashPin } from '@/lib/pinCrypto';

/**
 * Diálogo de recuperación simple del PIN de un perfil de trabajador (worker).
 *
 * Contexto de seguridad:
 * - Solo se monta/abre cuando la cuenta User autenticada ya ha sido validada
 *   por el llamador (AppHeader) como asignada exactamente a este profileId
 *   (accountMemberships/{uid}.assignedProfileId == profileId). Esta
 *   comprobación de UI es solo una conveniencia; la protección real e
 *   infalsificable la aplican las Firestore Rules sobre
 *   workspaces/{workspaceId}/workerProfiles/{profileId} (isWorkerAssigned).
 * - No pide el PIN anterior, ni contraseña de Firebase, ni email, ni WhatsApp.
 * - Solo permite CREAR un PIN nuevo que sustituye al anterior.
 * - Reutiliza exclusivamente updateOwnWorkerProfile (persistencia ya
 *   existente y validada en ConfigContext/Firestore Rules); no duplica
 *   lógica de escritura.
 */
export function ProfilePinRecoveryDialog({
  open,
  onOpenChange,
  profileId,
  profileName,
  updateOwnWorkerProfile,
  onSuccess,
}) {
  const { toast } = useToast();

  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [pinError, setPinError] = useState('');

  const resetState = () => {
    setNewPin('');
    setConfirmPin('');
    setPinError('');
    setIsSaving(false);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  // Limpiar datos sensibles del estado también al desmontar el componente
  useEffect(() => {
    return () => {
      resetState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    resetState();
    onOpenChange(false);
  };

  const validatePinFormat = (pin) => /^[0-9]{4}$/.test(pin);

  const handleSaveNewPin = async () => {
    setPinError('');

    if (!profileId) {
      setPinError('No se pudo identificar el perfil.');
      return;
    }

    if (typeof updateOwnWorkerProfile !== 'function') {
      setPinError('No se pudo restablecer el PIN. Inténtalo de nuevo.');
      return;
    }

    if (!validatePinFormat(newPin)) {
      setPinError('El PIN debe tener 4 dígitos.');
      return;
    }

    if (newPin !== confirmPin) {
      setPinError('Los PIN no coinciden.');
      return;
    }

    setIsSaving(true);
    try {
      const salt = await generateSalt();
      const { hash, iterations, version } = await hashPin(newPin, salt);

      const success = await updateOwnWorkerProfile(profileId, {
        pinHash: hash,
        pinSalt: salt,
        pinIterations: iterations,
        pinVersion: version,
        pinUpdatedAt: new Date().toISOString(),
      });

      if (!success) {
        // Guardado falló: el PIN antiguo sigue vigente, no se toca nada más.
        setPinError('No se pudo guardar el nuevo PIN. Inténtalo de nuevo.');
        setIsSaving(false);
        return;
      }

      toast({
        title: '✅ PIN actualizado',
        description: `Ya puedes usar el nuevo PIN de "${profileName || 'tu perfil'}".`,
        duration: 2500,
      });

      resetState();
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('[PROFILE_PIN_RECOVERY] Error saving new PIN:', error);
      setPinError('Error al guardar el nuevo PIN. Inténtalo de nuevo.');
      setIsSaving(false);
    }
  };

  const handlePinKeyPress = (e) => {
    if (e.key === 'Enter' && !isSaving) {
      handleSaveNewPin();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleCancel(); else onOpenChange(next); }}>
      <DialogContent
        className="max-w-sm"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-yellow-600" />
            Restablecer PIN de tu perfil
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            Solo tú puedes restablecer el PIN de tu perfil desde tu cuenta.
          </p>

          <div>
            <Label htmlFor="new-profile-pin" className="text-sm font-medium">
              Nuevo PIN
            </Label>
            <Input
              id="new-profile-pin"
              type="password"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value)}
              onKeyPress={handlePinKeyPress}
              placeholder="0000"
              className="mt-1 text-center text-2xl tracking-widest"
              maxLength="4"
              disabled={isSaving}
            />
          </div>

          <div>
            <Label htmlFor="confirm-profile-pin" className="text-sm font-medium">
              Repite el nuevo PIN
            </Label>
            <Input
              id="confirm-profile-pin"
              type="password"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value)}
              onKeyPress={handlePinKeyPress}
              placeholder="0000"
              className="mt-1 text-center text-2xl tracking-widest"
              maxLength="4"
              disabled={isSaving}
            />
          </div>

          {pinError && (
            <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{pinError}</span>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveNewPin}
            disabled={isSaving || !newPin || !confirmPin}
            className="bg-primary hover:bg-primary/90"
          >
            {isSaving ? 'Guardando...' : 'Restablecer PIN'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
